import React, { useState } from 'react';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, IconButton, Avatar,
  Menu, MenuItem, Divider, Chip, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Dashboard, People, EventNote, BeachAccess, CalendarMonth, Event,
  Menu as MenuIcon, Logout, Notifications, TableChart,
  DirectionsBus, School, Assignment, Grade, MenuBook,
  AttachMoney, Restaurant, Article,
  EmojiEvents, QuestionAnswer,
  CreditCard, TrendingUp, Settings, AutoStories, LibraryBooks, Grading,
  AccountBalance,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';
import Circular from '../pages/circular/Circular';

const DRAWER_WIDTH = 260;

const ADMIN_ONLY = new Set([
  '/fees', '/fees-structure', '/users', '/leads', '/departments', '/institutions', '/exports', '/vehicles', 
  // '/escort-cards', '/notifications',
]);


const ACCOUNTANT_ONLY = new Set([
  '/fees', '/fees-structure', 
]);

const navItems = [
  { label: 'Dashboard',       icon: <Dashboard />,       path: '/dashboard' },
  { label: 'Students',        icon: <School />,          path: '/students' },
  { label: 'Attendance',      icon: <EventNote />,       path: '/attendance' },
  { label: 'Leave',           icon: <BeachAccess />,     path: '/leave' },
  { label: 'Fees',            icon: <AttachMoney />,     path: '/fees' },
  { label: 'Fees Structure',  icon: <AttachMoney />,     path: '/fees-structure' },
  { label: 'Assignments',     icon: <Assignment />,      path: '/assignments' },
  { label: 'Study Materials', icon: <LibraryBooks />,    path: '/study-materials' },
  { label: 'Learning Progress', icon: <Grading />,       path: '/learning-modules' },
  { label: 'Results',         icon: <Grade />,           path: '/results' },
  { label: 'Subjects',        icon: <MenuBook />,        path: '/subjects' },
  { label: 'Exam Types',      icon: <Grade />,           path: '/exams' },
  { label: 'Examination',     icon: <Grading />,         path: '/examination' },
  { label: 'Timetable',       icon: <CalendarMonth />,   path: '/timetable' },
  { label: 'Doubts',          icon: <QuestionAnswer />,  path: '/doubts' },
  // { label: 'Vehicles',        icon: <DirectionsBus />,path: '/vehicles' },
  { label: 'Escort Cards',    icon: <CreditCard />,      path: '/escort-cards' },
  { label: 'Notifications',   icon: <Notifications />,   path: '/notifications' },
  { label: 'Circular',        icon: <Notifications />,   path: '/circular' },
  { label: 'Out Reach Activities', icon: <Article />,    path: '/content' },
  // { label: 'Stories',         icon: <AutoStories />,  path: '/stories' },
  // { label: 'Kids Activities', icon: <EmojiEvents />,  path: '/activities' },
  { label: 'Holidays',        icon: <CalendarMonth />,   path: '/holidays' },
  { label: 'Academic Calendar', icon: <Event />,         path: '/academic-calendar' },
  { label: 'Food Menu',       icon: <Restaurant />,      path: '/food-menu' },
  { label: 'Leads',           icon: <TrendingUp />,      path: '/leads' },
  { label: 'Users',           icon: <People />,          path: '/users' },
  { label: 'Departments',     icon: <Settings />,        path: '/departments' },
  { label: 'Institutions',    icon: <AccountBalance />,  path: '/institutions' },
  { label: 'Exports',         icon: <TableChart />,      path: '/exports' },
];

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const isTeacher = user?.role === 'Teacher';
  const isAccountant = user?.role === 'Accountant';
  const visibleNavItems = isAccountant ? navItems.filter(item => ACCOUNTANT_ONLY.has(item.path)) : isTeacher
    ? navItems.filter(item => !ADMIN_ONLY.has(item.path))
    : navItems;

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'linear-gradient(180deg, #1a237e 0%, #0d47a1 100%)', overflowY: 'auto' }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <Box sx={{ width: 42, height: 42, borderRadius: 2, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.5, flexShrink: 0 }}>
          <img src={logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }} />
        </Box>
        <Box>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 15, lineHeight: 1.2 }}>Shree Bhavani Group Of Institutions</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>Admin Panel</Typography>
        </Box>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mx: 2 }} />
      <Box sx={{ px: 2, py: 1 }}>
        <Chip label={user?.role || 'Admin'} size="small" sx={{ background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: 11 }} />
      </Box>
      <List sx={{ flex: 1, px: 1.5, pt: 0 }}>
        {visibleNavItems.map((item, idx) => {
          const active = location.pathname === item.path;
          const sectionBreaks = ['/assignments', '/fees', '/vehicles', '/notifications', '/holidays', '/leads'];
          const showDivider = idx > 0 && sectionBreaks.includes(item.path);
          return (
            <React.Fragment key={item.path}>
              {showDivider && <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 0.8, mx: 1 }} />}
              <ListItem disablePadding sx={{ mb: 0.3 }}>
                <ListItemButton
                  onClick={() => { navigate(item.path); setMobileOpen(false); }}
                  sx={{ borderRadius: 2, py: 1, background: active ? 'rgba(255,255,255,0.2)' : 'transparent', '&:hover': { background: 'rgba(255,255,255,0.12)' }, border: active ? '1px solid rgba(255,255,255,0.25)' : '1px solid transparent' }}
                >
                  <ListItemIcon sx={{ color: active ? '#fff' : 'rgba(255,255,255,0.65)', minWidth: 36 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13, fontWeight: active ? 700 : 500, color: active ? '#fff' : 'rgba(255,255,255,0.75)' }} />
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mx: 2 }} />
      <List sx={{ px: 1.5, pb: 2 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2, py: 1, '&:hover': { background: 'rgba(255,100,100,0.2)' } }}>
            <ListItemIcon sx={{ color: 'rgba(255,255,255,0.65)', minWidth: 36 }}><Logout /></ListItemIcon>
            <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.75)' }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f5f6fa' }}>
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer variant={isMobile ? 'temporary' : 'permanent'} open={isMobile ? mobileOpen : true} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', border: 'none', boxShadow: '4px 0 24px rgba(26,35,126,0.15)' } }}>
          {drawerContent}
        </Drawer>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="sticky" elevation={0} sx={{ background: '#fff', borderBottom: '1px solid #e8eaf6', color: '#1a237e' }}>
          <Toolbar sx={{ gap: 1 }}>
            {isMobile && <IconButton onClick={() => setMobileOpen(true)} sx={{ color: '#1a237e' }}><MenuIcon /></IconButton>}
            <Typography variant="h6" fontWeight={700} sx={{ flex: 1, color: '#1a237e', fontSize: { xs: 15, sm: 18 } }}>
              {navItems.find((n) => n.path === location.pathname)?.label || "Shree Bhavani Group Of Institutions"}
            </Typography>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #1a237e, #1565c0)', fontSize: 14, fontWeight: 700 }}>
                {user?.role?.[0] || 'A'}
              </Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
              PaperProps={{ sx: { borderRadius: 2, minWidth: 180, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography fontWeight={700} fontSize={14}>{user?.role}</Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main', gap: 1 }}><Logout fontSize="small" /> Logout</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>{children}</Box>
      </Box>
    </Box>
  );
}
